import type { CarbonSource, Sector } from '../../lib/assessment/types'

export const CARBON_SOURCES: CarbonSource[] = [
  // Scope 1
  {
    id: 's1_mobile',
    scope: '1',
    label: 'Owned or controlled vehicles and mobile equipment',
    helpText: 'Company cars, trucks, forklifts, construction equipment under operational control.',
  },
  {
    id: 's1_stationary',
    scope: '1',
    label: 'Boilers, generators and stationary fuel combustion',
  },
  {
    id: 's1_process',
    scope: '1',
    label: 'Industrial process emissions',
    sectorInclude: ['manufacturing', 'oil_gas_energy', 'agriculture'],
  },
  {
    id: 's1_refrigerants',
    scope: '1',
    label: 'Refrigerants and air-conditioning equipment (fugitive)',
  },
  {
    id: 's1_other_fuel',
    scope: '1',
    label: 'Other company-controlled fuel use',
  },
  {
    id: 's1_construction_equipment',
    scope: '1',
    label: 'Construction site equipment fuel (owned/controlled)',
    sectorInclude: ['construction'],
    helpText: 'Separate from third-party subcontractor equipment where you do not have operational control.',
  },
  // Scope 2
  {
    id: 's2_electricity',
    scope: '2',
    label: 'Purchased electricity',
  },
  {
    id: 's2_cooling_heating',
    scope: '2',
    label: 'Purchased district cooling / heating (where applicable)',
    sectorInclude: [
      'real_estate',
      'hospitality',
      'retail',
      'healthcare',
      'technology_services',
      'financial_services',
      'manufacturing',
    ],
  },
  {
    id: 's2_tenant_landlord_split',
    scope: '2',
    label: 'Tenant vs landlord energy boundary (shared buildings)',
    sectorInclude: ['real_estate', 'retail', 'hospitality', 'technology_services'],
    helpText: 'Assess whether landlord and tenant meters/bills are mapped to the reporting boundary.',
  },
  // Scope 3
  {
    id: 's3_purchased_goods',
    scope: '3',
    label: 'Purchased goods and services',
  },
  {
    id: 's3_capital_goods',
    scope: '3',
    label: 'Capital goods',
    sectorInclude: ['manufacturing', 'construction', 'real_estate', 'oil_gas_energy', 'healthcare'],
  },
  {
    id: 's3_construction_materials',
    scope: '3',
    label: 'Construction materials / embodied carbon data',
    sectorInclude: ['construction', 'real_estate'],
    helpText: 'Cement, steel, aluminium and other high-impact materials; EPDs where available.',
  },
  {
    id: 's3_fuel_energy',
    scope: '3',
    label: 'Fuel- and energy-related activities (not in Scope 1 or 2)',
  },
  {
    id: 's3_upstream_transport',
    scope: '3',
    label: 'Upstream transportation and distribution',
    sectorInclude: ['manufacturing', 'retail', 'logistics', 'construction', 'agriculture'],
  },
  {
    id: 's3_third_party_transport',
    scope: '3',
    label: 'Third-party / outsourced transport services',
    sectorInclude: ['logistics', 'retail', 'manufacturing', 'construction'],
  },
  {
    id: 's3_waste',
    scope: '3',
    label: 'Waste generated in operations',
  },
  {
    id: 's3_food_waste',
    scope: '3',
    label: 'Food waste and related disposal',
    sectorInclude: ['hospitality', 'healthcare', 'retail'],
  },
  {
    id: 's3_business_travel',
    scope: '3',
    label: 'Business travel',
  },
  {
    id: 's3_commuting',
    scope: '3',
    label: 'Employee commuting',
  },
  {
    id: 's3_downstream_transport',
    scope: '3',
    label: 'Downstream transportation and distribution',
    sectorInclude: ['manufacturing', 'retail', 'logistics', 'agriculture'],
  },
  {
    id: 's3_use_of_products',
    scope: '3',
    label: 'Use of sold products (where applicable)',
    sectorInclude: ['manufacturing', 'oil_gas_energy', 'technology_services'],
  },
  {
    id: 's3_eol_products',
    scope: '3',
    label: 'End-of-life treatment of sold products',
    sectorInclude: ['manufacturing', 'retail', 'technology_services'],
  },
  {
    id: 's3_investments',
    scope: '3',
    label: 'Investments / financed emissions (where relevant)',
    sectorInclude: ['financial_services'],
  },
  {
    id: 's3_leased_assets',
    scope: '3',
    label: 'Upstream or downstream leased assets',
    sectorInclude: ['real_estate', 'logistics', 'retail', 'hospitality', 'financial_services'],
  },
  {
    id: 's3_cloud_datacentre',
    scope: '3',
    label: 'Cloud and data-centre services',
    sectorInclude: ['technology_services', 'financial_services', 'healthcare', 'retail'],
    helpText: 'Supplier energy/GHG reports or spend-based screening for hosted infrastructure.',
  },
  {
    id: 's3_subcontractors',
    scope: '3',
    label: 'Subcontractor activities (fuel, energy, materials)',
    sectorInclude: ['construction', 'oil_gas_energy', 'logistics'],
  },
]

export function carbonSourcesForSector(sector: Sector | ''): CarbonSource[] {
  return CARBON_SOURCES.filter((s) => {
    if (!s.sectorInclude) return true
    if (!sector) return true
    return s.sectorInclude.includes(sector)
  })
}
